// types
import { AuditChangeEventResponse, EventResponse } from 'generated/index';

// utils
import dayjs from 'dayjs';
import i18n from 'config/i18n';
import { getLocale } from 'utils/date';

const t = i18n.getFixedT(getLocale(), 'common', 'mrp');

export const getUndoCommandTitle = (
  commandType: string | undefined,
  createdAt: string | undefined,
  originalCommandType?: string
): string => {
  if (!commandType || !createdAt) {
    return '';
  }

  if (commandType === 'UndoCommand' && originalCommandType) {
    const commandTypeToTranslation: Record<string, string> = {
      CreateAppointmentCommand: 'UndoneCreateAppointmentCommand',
      DeleteAppointmentCommand: 'UndoneDeleteAppointmentCommand',
      UpdateAppointmentCommand: 'UndoneUpdateAppointmentCommand'
    };

    const translationKey =
      commandTypeToTranslation[originalCommandType] ||
      'UndoneCreateAppointmentCommand';

    return (
      t(`contextPanel.commandType.${translationKey}`) +
      ' ' +
      dayjs(createdAt).format('HH:mm')
    );
  }

  return (
    t(`contextPanel.commandType.${commandType}`) +
    ' ' +
    dayjs(createdAt).format('HH:mm')
  );
};

const getSingularEventFromChangeEventOrSeries = (
  changeEvent: AuditChangeEventResponse
): Partial<EventResponse> => {
  const { afterState, beforeState, type } = changeEvent;

  // For single appointments, return the state directly
  if (type === 'APPOINTMENT') {
    return afterState || beforeState || {};
  }

  // For appointment series, extract the last appointment from the series
  const afterEvent =
    afterState?.appointments?.[afterState.appointments.length - 1];
  const beforeEvent =
    beforeState?.appointments?.[beforeState.appointments.length - 1];

  return afterEvent || beforeEvent || {};
};

export const getChangedEventPreviewLabel = (
  changeEvent: AuditChangeEventResponse | undefined
): string => {
  if (!changeEvent) return '';

  const { start, activity } =
    getSingularEventFromChangeEventOrSeries(changeEvent);

  const parts: string[] = [];

  // Add date in format DD-MM-YYYY
  if (start && dayjs(start).isValid()) {
    parts.push(dayjs(start).format('DD-MM-YYYY'));
  }

  // Add time in format HH:mm
  if (start && dayjs(start).isValid()) {
    parts.push(dayjs(start).format('HH:mm'));
  }

  // Add activity code or description
  const activityLabel = activity?.code || activity?.description;
  if (activityLabel?.trim()) {
    parts.push(activityLabel.trim());
  }

  return parts.join(' • ');
};

export const getLocationInfo = (
  changeEvent: AuditChangeEventResponse | undefined
): string | null => {
  if (!changeEvent) return null;

  const { workPlace, location } =
    getSingularEventFromChangeEventOrSeries(changeEvent);

  if (workPlace?.name) {
    return workPlace.name;
  }

  if (location?.address) {
    const { streetName, houseNumber, postalCode, city } = location.address;
    const parts = [
      streetName && houseNumber ? `${streetName} ${houseNumber}` : null,
      postalCode && city ? `${postalCode} ${city}` : null
    ].filter(Boolean);

    if (parts.length > 0) return parts.join(', ');
  }

  return null;
};

export const getEmployeeInfo = (
  changeEvent: AuditChangeEventResponse | undefined
): string[] => {
  if (!changeEvent) return [];

  const eventData = getSingularEventFromChangeEventOrSeries(changeEvent);
  const { employees } = eventData;

  const employeeList = (employees || [])
    .map((emp: any) => emp?.nameInformal || emp?.name || '')
    .filter(Boolean);

  if (employeeList.length > 1) {
    const key = 'employees';
    return [`${employeeList.length} ${t(`appointmentForm.${key}`)}`];
  }

  return employeeList;
};

export const getClientInfo = (
  changeEvent: AuditChangeEventResponse | undefined
): string[] => {
  if (!changeEvent) return [];

  const eventData = getSingularEventFromChangeEventOrSeries(changeEvent);
  const { clients } = eventData;

  const clientList = (clients || [])
    .map((client: any) => client?.fullName || '')
    .filter(Boolean);

  if (clientList.length > 1) {
    const key = 'clients';
    return [`${clientList.length} ${t(`appointmentForm.${key}`)}`];
  }

  return clientList;
};

export const getEffectiveChangesFromChangeEventOrSeries = (
  changeEvent: AuditChangeEventResponse | undefined
): Record<string, { oldValue: string; newValue: string }> => {
  const isSeries = changeEvent?.type === 'APPOINTMENT_SERIES';

  const changedProps: Record<string, { oldValue: string; newValue: string }> =
    isSeries
      ? changeEvent?.effectiveChanges?.appointments?.[
          Object.keys(changeEvent.effectiveChanges.appointments || {})?.[0]
        ]
      : undefined;

  return changedProps || changeEvent?.effectiveChanges || {};
};

export const flattenNestedChangedProperties = (
  changedProps: Record<string, { oldValue: string; newValue: string }>
) => {
  Object.entries(changedProps).forEach(([key, value]) => {
    //  If value itself has oldValue/newValue directly → flatten it normally
    if ('newValue' in value || 'oldValue' in value) {
      changedProps[key] = {
        oldValue: String(value.oldValue ?? ''),
        newValue: String(value.newValue ?? '')
      };
      return;
    }

    // Otherwise, search recursively for the first nested old/new value pair
    const findNestedValues = (
      obj: any
    ): { oldValue?: any; newValue?: any } | null => {
      if (!obj || typeof obj !== 'object') return null;

      if ('oldValue' in obj || 'newValue' in obj) {
        return {
          oldValue: obj.oldValue,
          newValue: obj.newValue
        };
      }

      for (const val of Object.values(obj)) {
        const found = findNestedValues(val);
        if (found) return found;
      }
      return null;
    };

    const nested = findNestedValues(value);
    if (nested) {
      changedProps[key] = {
        oldValue: String(nested.oldValue ?? ''),
        newValue: String(nested.newValue ?? '')
      };
    } else {
      // fallback — use first value of object if nothing found
      const firstValue = Object.values(value)[0];
      changedProps[key] = {
        oldValue: String(firstValue ?? ''),
        newValue: String(firstValue ?? '')
      };
    }
  });
};

export const getMappedChangedProperties = (
  changeEvent: AuditChangeEventResponse | undefined
): Record<string, { oldValue: string; newValue: string }> => {
  const changedProps = getEffectiveChangesFromChangeEventOrSeries(changeEvent);

  // Remove appointmentExceptions (temporary logic)
  let filtered = { ...changedProps };
  if (filtered['appointmentExceptions']) {
    delete filtered['appointmentExceptions'];

    if (Object.keys(filtered).length === 0) {
      filtered = {
        appointmentExceptions: {
          oldValue: `${t('appointmentExceptions')}`,
          newValue: `${t('appointmentExceptions')}`
        }
      };
    }
  }

  const findNestedValues = (
    obj: any
  ): { oldValue?: any; newValue?: any } | null => {
    if (!obj || typeof obj !== 'object') return null;

    // Handle direct newValue/oldValue
    if ('oldValue' in obj || 'newValue' in obj) {
      return {
        oldValue: obj.oldValue || '-',
        newValue: obj.newValue || '-'
      };
    }

    // Handle CREATE case (newObject only)
    if ('newObject' in obj) {
      const firstVal =
        obj.newObject?.code ||
        obj.newObject?.description ||
        obj.newObject?.name ||
        Object.values(obj.newObject || {})[0];
      return { oldValue: firstVal, newValue: firstVal };
    }

    // Recurse into other nested structures
    for (const val of Object.values(obj)) {
      const found = findNestedValues(val);
      if (found) return found;
    }

    return null;
  };

  const flattened = Object.entries(filtered).reduce(
    (acc, [key, value]) => {
      const nested = findNestedValues(value);

      acc[key] = {
        oldValue: String(nested?.oldValue ?? '-'),
        newValue: String(nested?.newValue ?? '-')
      };

      return acc;
    },
    {} as Record<string, { oldValue: string; newValue: string }>
  );

  return flattened;
};
